import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "shared/components/common/customButton";
import CustomPagination from "shared/components/common/customPagination";
import CustomRadioButton from "shared/components/common/customRadioButton";
import CustomSelectTable from "shared/components/common/customSelectTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import FilterSideCanvas from "shared/components/filtersSideCanvas";
import useDebounce from "shared/hooks/useDebounce";
import SekeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import { setArchiveCountReducer } from "shared/redux/reducers/archiveCount";
import { routeConstant } from "shared/routes/routeConstant";
import {
  ArchiveEvaluationsService,
  DownloadAllEvaluationsService,
  DownloadEvaluationsService,
  FlagEvaluationsService,
  GetAllEvaluationsService,
  GetArchiveCountService,
} from "shared/services/evaluations";
import styles from "./styles.module.scss";
import { exportBulkToExcel } from "shared/services/exportBulkExcel";
import { setEvaluationsReducer } from "shared/redux/reducers/evaluations";

const Evaluations = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const allEvaluationsRef = useRef<any[]>([]);
  const filtersRef = useRef<any>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [archiveLoading, setArchiveLoading] = useState<boolean>(false);
  const [downlaodLoading, setDownlaodLoading] = useState<boolean>(false);
  const [evalIds, setEvalIds] = useState<string[]>([]);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [search, setSearch] = useState<string>("");
  const [searchVal, setSearchVal] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [filters, setFilters] = useState<any>(null);
  const [initial, setInitial] = useState<boolean>(true);
  const [sortBy, setSortBy] = useState<string>("");
  const [order, setOrder] = useState<string>("");
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const handleGetCount = () => {
    GetArchiveCountService()
      .then(({ data }) => {
        dispatch(setArchiveCountReducer({ count: data }));
      })
      .catch((err) => {});
  };

  const handleGetEvaluations = (page: number) => {
    setLoading(true);
    GetAllEvaluationsService({
      search: searchVal,
      pageNo: page,
      filter: filters,
      sortBy,
      order,
    })
      .then(({ data: { data, total } }) => {
        setEvaluations(data);
        setTotal(total);
        allEvaluationsRef.current = [...allEvaluationsRef.current, ...data];
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const handleArchiveEvaluation = () => {
    setArchiveLoading(true);
    ArchiveEvaluationsService({ evalIds: evalIds })
      .then(() => {
        toastMessage("success", "Evaluation archived successfully");
        let tempEvals = evaluations.filter(
          (item) => !evalIds.includes(item.id)
        );
        setEvaluations(tempEvals);
        handleGetCount();
        setEvalIds([]);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setArchiveLoading(false);
      });
  };

  const handleMultipleEvaluation = () => {
    let selectedEvals = allEvaluationsRef.current.filter((evaluation) =>
      evalIds.includes(evaluation.id)
    );
    dispatch(setEvaluationsReducer({ evaluations: selectedEvals }));
    navigate(routeConstant.multipleEvaluationRecordDetail.path);
  };

  const handleDownlaodEvals = () => {
    setDownlaodLoading(true);
    DownloadEvaluationsService({
      evalIds: evalIds,
    })
      .then(({ data, status }) => {
        if (status) {
          exportBulkToExcel(data);
        }
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setDownlaodLoading(false);
      });
  };

  const handleDownloadAll = () => {
    setDownloadLoading(true);
    DownloadAllEvaluationsService({
      search: searchVal,
      pageNo: page,
      filter: filters,
      sortBy,
      order,
    })
      .then(({ data, status }) => {
        if (status) {
          exportBulkToExcel(data);
        }
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  useEffect(() => {
    let filtersObj = {
      startDate:
        queryParams.get("startDate") !== "undefined" &&
        queryParams.get("startDate") !== null
          ? queryParams.get("startDate")
          : null,
      endDate:
        queryParams.get("endDate") !== "undefined" &&
        queryParams.get("endDate") !== null
          ? queryParams.get("endDate")
          : null,
      status:
        queryParams.get("status") !== "undefined" &&
        queryParams.get("status") !== null
          ? queryParams.get("status")
          : null,
      user:
        queryParams.get("user") !== "undefined" &&
        queryParams.get("user") !== null
          ? queryParams.get("user")
          : null,
      jobTitle:
        queryParams.get("jobTitle") !== "undefined" &&
        queryParams.get("jobTitle") !== null
          ? queryParams.get("jobTitle")
          : "",
      department:
        queryParams.get("department") !== "undefined" &&
        queryParams.get("department") !== null
          ? queryParams.get("department")
          : "",
      jobCode:
        queryParams.get("jobCode") !== "undefined" &&
        queryParams.get("jobCode") !== null
          ? queryParams.get("jobCode")
          : "",
      jobGrade:
        queryParams.get("jobGrade") !== "undefined" &&
        queryParams.get("jobGrade") !== null
          ? queryParams.get("jobGrade")
          : "",
      roleType:
        queryParams.get("roleType") !== "undefined" &&
        queryParams.get("roleType") !== null
          ? queryParams.get("roleType")
          : null,
      modifier:
        queryParams.get("modifier") !== "undefined" &&
        queryParams.get("modifier") !== null
          ? queryParams.get("modifier")
          : null,
      levelOfWork:
        queryParams.get("levelOfWork") !== "undefined" &&
        queryParams.get("levelOfWork") !== null
          ? queryParams.get("levelOfWork")
          : null,
      q2:
        queryParams.get("q2") !== "undefined" && queryParams.get("q2") !== null
          ? queryParams.get("q2")
          : null,
      q3:
        queryParams.get("q3") !== "undefined" && queryParams.get("q3") !== null
          ? queryParams.get("q3")
          : null,
      q4:
        queryParams.get("q4") !== "undefined" && queryParams.get("q4") !== null
          ? queryParams.get("q4")
          : null,
      q5:
        queryParams.get("q5") !== "undefined" && queryParams.get("q5") !== null
          ? queryParams.get("q5")
          : null,
      q6:
        queryParams.get("q6") !== "undefined" && queryParams.get("q6") !== null
          ? queryParams.get("q6")
          : null,
      q7:
        queryParams.get("q7") !== "undefined" && queryParams.get("q7") !== null
          ? queryParams.get("q7")
          : null,
      q8:
        queryParams.get("q8") !== "undefined" && queryParams.get("q8") !== null
          ? queryParams.get("q8")
          : null,
      minScore:
        queryParams.get("minScore") !== "undefined" &&
        queryParams.get("minScore") !== null
          ? queryParams.get("minScore")
          : null,
      maxScore:
        queryParams.get("maxScore") !== "undefined" &&
        queryParams.get("maxScore") !== null
          ? queryParams.get("maxScore")
          : null,
      minLvl:
        queryParams.get("minLvl") !== "undefined" &&
        queryParams.get("minLvl") !== null
          ? queryParams.get("minLvl")
          : null,
      maxLvl:
        queryParams.get("maxLvl") !== "undefined" &&
        queryParams.get("maxLvl") !== null
          ? queryParams.get("maxLvl")
          : null,
    };
    filtersRef.current = filtersObj;
    setFilters(filtersObj);
    setInitial(false);
    // eslint-disable-next-line
  }, [location.search]);

  useEffect(() => {
    if (!initial) {
      setPage(1);
      setEvaluations([]);
      setLoading(true);
      handleGetEvaluations(1);
    }
    // eslint-disable-next-line
  }, [searchVal, filters, sortBy, order]);

  useDebounce(
    () => {
      setSearchVal(search);
    },
    [search],
    800
  );

  return (
    <div className={classNames(styles.mainContainer)}>
      <div
        className={classNames(
          "flex items-start gap-3  justify-between flex-col lg:flex-row px-4 py-4",
          styles.tableMainHead
        )}
      >
        <div
          className={classNames(
            "flex w-full lg:w-2/5  2xl:w-1/5 flex-col items-start"
          )}
        >
          <h1 className={classNames(styles.tableMainHeading)}>
            Evaluation Record History
          </h1>
          {!loading ? (
            <p className={classNames(styles.tableMainTitle)}>
              Showing results {(page - 1) * 10 + 1} -{" "}
              {(page - 1) * 10 + evaluations?.length} of {total}
            </p>
          ) : (
            <p className={classNames(styles.tableMainTitle)}>
              Showing all Evaluations
            </p>
          )}
        </div>
        <div
          className={classNames(
            "flex flex-wrap w-full lg:w-5/6 items-center justify-end gap-3"
          )}
        >
          {evalIds?.length > 0 ? (
            <>
              <label className={classNames(styles.evalCountLabel)}>
                {String(evalIds?.length).padStart(2, "0")} Evaluation
                {evalIds?.length > 1 ? "s " : " "}
                Selected
              </label>
              <CustomButton
                Icon={Icons.Archive}
                IconDirction="left"
                title="Archive"
                containerStyle={classNames(styles.buttonStyle)}
                onClick={handleArchiveEvaluation}
                loading={archiveLoading}
                disabled={archiveLoading}
              />
              <CustomButton
                Icon={Icons.Eye}
                IconDirction="left"
                title="View"
                containerStyle={classNames(styles.buttonStyle)}
                onClick={handleMultipleEvaluation}
              />
              <CustomButton
                Icon={Icons.Downlaod}
                IconDirction="left"
                title="Download"
                loading={downlaodLoading}
                disabled={downlaodLoading}
                containerStyle={classNames(styles.buttonStyle)}
                onClick={handleDownlaodEvals}
              />
            </>
          ) : null}

          <div className={classNames(styles.searchContainer, "flex")}>
            <Icons.Search className={classNames(styles.searchIconStyle)} />
            <input
              className={classNames(styles.searchInputStyle, "ps-2")}
              value={search}
              placeholder="Search Job Title / Dept"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
          <CustomButton
            title="Filter"
            containerStyle={classNames(styles.buttonMain)}
            Icon={Icons.Filter}
            IconDirction="left"
            iconStyle={classNames(styles.buttonIcon)}
            onClick={() => {
              setOpenFilters(!openFilters);
            }}
          />
          <CustomButton
            title="Download All"
            loading={downloadLoading}
            disabled={downloadLoading}
            containerStyle={classNames(styles.buttonStyle)}
            onClick={handleDownloadAll}
          />
        </div>
      </div>
      <CustomSelectTable
        evaluations={evaluations}
        evalIds={evalIds}
        title={"Evaluation Record History"}
        isEmpty={evaluations.length === 0}
        loading={loading}
        setEvalIds={setEvalIds}
        setSortBy={setSortBy}
        setOrder={setOrder}
        sortBy={sortBy}
        order={order}
      >
        {loading ? (
          <>
            {Array.from(Array(5).keys()).map((_, index) => {
              return <TableRowLoader key={index} />;
            })}
          </>
        ) : (
          <>
            {evaluations.map((evaluation, index) => {
              return (
                <TableRow
                  item={evaluation}
                  key={evaluation?.id}
                  index={index}
                  evalIds={evalIds}
                  setEvalIds={setEvalIds}
                  handleGetCount={handleGetCount}
                />
              );
            })}
          </>
        )}
      </CustomSelectTable>

      <div
        className={classNames(
          "w-full px-4 py-4",
          total >= 10 && styles.border,
          styles.paginationContainer
        )}
      >
        <CustomPagination
          currentPage={page}
          totalCount={total}
          pageSize={10}
          onPageChange={(page: any) => {
            setPage(page);
            handleGetEvaluations(page);
          }}
        />
      </div>
      <FilterSideCanvas
        isOpen={openFilters}
        setIsOpen={setOpenFilters}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        isShowColumns={false}
        routePath={routeConstant.evaluationRecord.path}
        parentFilters={filtersRef.current}
      />
    </div>
  );
};

const TableRow = ({ item, evalIds, setEvalIds, handleGetCount }: any) => {
  const [flagged, setFlagged] = useState<boolean>(item?.flagged);
  const [showConfirmation, setConfirmation] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleOpenConfirmation = () => {
    setConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmation(false);
  };

  const handleWatchLater = () => {
    setLoading(true);
    FlagEvaluationsService({ id: item?.id })
      .then(() => {
        handleGetCount();
        toastMessage("success", "Evaluation flagged successfully");
        setFlagged(true);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setLoading(false);
        setConfirmation(false);
      });
  };

  return (
    <>
      <tr>
        <td
          className={classNames(
            styles.tableContentLabel,
            "ps-2 pe-2 py-3 sticky-col check-box-first-col"
          )}
          style={{ verticalAlign: "middle" }}
        >
          <div className={classNames("flex items-center justify-center")}>
            <CustomRadioButton
              active={evalIds.includes(item?.id)}
              className={classNames(styles.customRadioStyle2)}
              onClick={() => {
                if (evalIds.includes(item?.id)) {
                  setEvalIds(evalIds.filter((id: any) => id !== item?.id));
                } else {
                  setEvalIds([...evalIds, item?.id]);
                }
              }}
            />
          </div>
        </td>

        <td
          className={classNames(
            styles.tableContentLabel,
            "px-2 py-3 sticky-col check-box-second-col"
          )}
        >
          {item?.current_version?.jobTitle}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "px-2 py-3")}
          style={{ maxWidth: "200px" }}
        >
          {item?.current_version?.department}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "px-2 py-3")}
          style={{ maxWidth: "100px" }}
        >
          {item?.jobCode}
        </td>

        <td
          className={classNames(
            styles.tableContentLabel,
            styles.highlighted,
            "px-2 py-3"
          )}
        >
          {item?.current_version?.rasScore}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.fromAdmin
            ? `CGP (${item?.evaluated_by?.name})`
            : item?.evaluated_by?.name}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {moment(item?.current_version?.created_at).format("MMM DD, YYYY")}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-4 py-3")}
          style={{ verticalAlign: "middle", textAlign: "center" }}
        >
          <div className={classNames("flex items-center gap-2 justify-center")}>
            <CustomToolTip label="See Details">
              <div
                className={classNames(styles.actionContainer)}
                onClick={() => {
                  navigate(
                    routeConstant.evaluationRecordDetail.path.replace(
                      ":id",
                      item?.id
                    )
                  );
                }}
              >
                <Icons.Eye />
              </div>
            </CustomToolTip>
            {!flagged ? (
              <CustomToolTip label="Flag">
                <div
                  className={classNames(styles.actionContainer)}
                  onClick={handleOpenConfirmation}
                >
                  <Icons.FlagOutline />
                </div>
              </CustomToolTip>
            ) : null}
          </div>
        </td>
      </tr>
      <ConfirmationModal
        handleSubmit={handleWatchLater}
        show={showConfirmation}
        handleClose={handleCloseConfirmation}
        title="Are you sure you want to flag this evaluation?"
        showSubtitle={false}
        actionButtonText={"Flag"}
        loading={loading}
      />
    </>
  );
};
const TableRowLoader = () => {
  return (
    <>
      <tr>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-2 py-3")}
          style={{ verticalAlign: "middle" }}
        >
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>

        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-2 py-3")}
          style={{ verticalAlign: "middle" }}
        >
          <div className={classNames("flex items-center justify-center gap-2")}>
            <SekeletonLoader
              className={classNames(styles.actionContainerLoader)}
            />
            <SekeletonLoader
              className={classNames(styles.actionContainerLoader)}
            />
          </div>
        </td>
      </tr>
    </>
  );
};

export default Evaluations;
